import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { 
  CaretRight, 
  CaretDown, 
  FolderOpen, 
  Folder,
  Chat,
  AndroidLogo,
  Envelope,
  Globe,
  WebhooksLogo,
  GlobeX,
  PhoneCall,
  FlowArrow,
  Plus,
  MagnifyingGlass,
  ShieldWarning,
  CircleNotch,
  BookOpen,
  ChalkboardSimple,
  Database,
  Wrench,
  GitBranch,
  PhoneTransfer,
  Notepad,
  Hash,
  MapPin,
  MapPinArea,
  DotsThree,
  DotsThreeVertical,
  PencilSimple,
  Trash,
  Copy,
  Gear,
  FolderPlus,
  Users,
  ArrowLeft,
  ArrowRight,
  Truck,
  Link,
  LinkBreak,
  Timer,  // Add Timer icon for Post Call Actions
  ChartBar,  // Add ChartBar icon for AI Coverage
  Lightbulb,  // Add Lightbulb icon for Tutorial Button
  Microphone,  // Add Microphone icon for Test AI Button
  Funnel, // Add Funnel icon for Qualifiers
  ChatTeardropText // Add ChatTeardropText icon for First Message
} from '@phosphor-icons/react';
import { Phone, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { CurbsideIcon } from '../utils/customIcons';
import { useBusinessType } from '../context/BusinessContext';
import { getSupabaseClient } from '../supabaseClient';
import { getIntegrationColors, getCurbsideColors } from '../utils/colorUtils';

// Coverage colors function - exact copy from Analysis.js
const getCoverageColors = (percentage) => {
  if (percentage === 0) {
    return {
      textColor: 'text-red-600',
      buttonColor: 'text-red-600',
      hoverColor: 'hover:bg-red-50',
      iconColor: 'text-red-600',
      iconBg: 'bg-red-100',
      background: 'bg-red-100',
      gradient: 'bg-red-500',
      height: 'h-1.5',
      isStripes: true,
      stripePattern: 'repeating-linear-gradient(45deg, #ef4444 0, #ef4444 4px, #fca5a5 4px, #fca5a5 8px)'
    };
  } else if (percentage >= 80) {
    return {
      textColor: 'text-green-600',
      buttonColor: 'text-green-600',
      hoverColor: 'hover:bg-green-50',
      iconColor: 'text-green-600',
      iconBg: 'bg-green-100',
      background: 'bg-green-100',
      gradient: 'bg-gradient-to-r from-green-400 to-green-500',
      height: 'h-1.5',
      isStripes: false
    };
  } else if (percentage >= 30) {
    return {
      textColor: 'text-yellow-600',
      buttonColor: 'text-yellow-600',
      hoverColor: 'hover:bg-yellow-50',
      iconColor: 'text-yellow-600',
      iconBg: 'bg-yellow-100',
      background: 'bg-yellow-100',
      gradient: 'bg-gradient-to-r from-yellow-400 to-yellow-500',
      height: 'h-1.5',
      isStripes: false
    };
  } else {
    return {
      textColor: 'text-red-600',
      buttonColor: 'text-red-600',
      hoverColor: 'hover:bg-red-50',
      iconColor: 'text-red-600',
      iconBg: 'bg-red-100',
      background: 'bg-red-100',
      gradient: 'bg-red-500',
      height: 'h-1.5',
      isStripes: false
    };
  }
};

// Dynamic Bar Chart Component
const DynamicBarChart = ({ percentage, size = 16, isLoading = false }) => {
  const colors = getCoverageColors(percentage);
  const uniqueId = `chart-${Math.random().toString(36).substr(2, 9)}`;

  // Show empty bars while loading (no animation)
  if (isLoading) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <defs>
          <linearGradient id={`emptyGradient-${uniqueId}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f3f4f6" />
            <stop offset="100%" stopColor="#e5e7eb" />
          </linearGradient>
          <filter id={`shadow-${uniqueId}`}>
            <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.05"/>
          </filter>
        </defs>
        {/* Empty bars - no animation until data loads */}
        <rect x="2" y="15" width="4" height="6" fill={`url(#emptyGradient-${uniqueId})`} rx="2" filter={`url(#shadow-${uniqueId})`} opacity="0.3" />
        <rect x="7" y="12" width="4" height="9" fill={`url(#emptyGradient-${uniqueId})`} rx="2" filter={`url(#shadow-${uniqueId})`} opacity="0.3" />
        <rect x="12" y="9" width="4" height="12" fill={`url(#emptyGradient-${uniqueId})`} rx="2" filter={`url(#shadow-${uniqueId})`} opacity="0.3" />
        <rect x="17" y="6" width="4" height="15" fill={`url(#emptyGradient-${uniqueId})`} rx="2" filter={`url(#shadow-${uniqueId})`} opacity="0.3" />
      </svg>
    );
  }

  // Determine number of bars to fill based on percentage
  const getFilledBars = () => {
    if (percentage === 0) return 0;
    if (percentage < 30) return 1;
    if (percentage < 50) return 2;
    if (percentage < 80) return 3;
    return 4;
  };

  const filledBars = getFilledBars();

  // Get color values for gradients
  const getGradientColors = (isFilled) => {
    if (!isFilled) return { start: '#f3f4f6', end: '#e5e7eb' };

    if (percentage < 30) return { start: '#f87171', end: '#dc2626' }; // Red gradient
    if (percentage < 50) return { start: '#fbbf24', end: '#d97706' }; // Yellow gradient  
    if (percentage < 80) return { start: '#fbbf24', end: '#d97706' }; // Yellow gradient
    return { start: '#34d399', end: '#059669' }; // Green gradient
  };

  // Special case for 0% - show elegant striped pattern
  if (percentage === 0) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <defs>
          {/* Beautiful diagonal stripe pattern */}
          <pattern id={`stripePattern-${uniqueId}`} patternUnits="userSpaceOnUse" width="3" height="3" patternTransform="rotate(45)">
            <rect width="1.5" height="3" fill="#ef4444" />
            <rect x="1.5" width="1.5" height="3" fill="#fca5a5" />
          </pattern>

          {/* Subtle shadow */}
          <filter id={`shadow-${uniqueId}`}>
            <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.1"/>
          </filter>
        </defs>

                 {/* Four elegant bars with perfect proportions and beautiful load animation */}
         <rect x="2" y="15" width="4" height="6" fill={`url(#stripePattern-${uniqueId})`} rx="2" filter={`url(#shadow-${uniqueId})`}>
           <animate attributeName="height" values="0;6" dur="0.6s" begin="0.1s" fill="freeze" />
           <animate attributeName="y" values="21;15" dur="0.6s" begin="0.1s" fill="freeze" />
           <animate attributeName="opacity" values="0;1" dur="0.3s" begin="0.1s" fill="freeze" />
         </rect>
         <rect x="7" y="12" width="4" height="9" fill={`url(#stripePattern-${uniqueId})`} rx="2" filter={`url(#shadow-${uniqueId})`}>
           <animate attributeName="height" values="0;9" dur="0.6s" begin="0.2s" fill="freeze" />
           <animate attributeName="y" values="21;12" dur="0.6s" begin="0.2s" fill="freeze" />
           <animate attributeName="opacity" values="0;1" dur="0.3s" begin="0.2s" fill="freeze" />
         </rect>
         <rect x="12" y="9" width="4" height="12" fill={`url(#stripePattern-${uniqueId})`} rx="2" filter={`url(#shadow-${uniqueId})`}>
           <animate attributeName="height" values="0;12" dur="0.6s" begin="0.3s" fill="freeze" />
           <animate attributeName="y" values="21;9" dur="0.6s" begin="0.3s" fill="freeze" />
           <animate attributeName="opacity" values="0;1" dur="0.3s" begin="0.3s" fill="freeze" />
         </rect>
         <rect x="17" y="6" width="4" height="15" fill={`url(#stripePattern-${uniqueId})`} rx="2" filter={`url(#shadow-${uniqueId})`}>
           <animate attributeName="height" values="0;15" dur="0.6s" begin="0.4s" fill="freeze" />
           <animate attributeName="y" values="21;6" dur="0.6s" begin="0.4s" fill="freeze" />
           <animate attributeName="opacity" values="0;1" dur="0.3s" begin="0.4s" fill="freeze" />
         </rect>
      </svg>
    );
  }

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <defs>
        {/* Elegant gradients for each bar state */}
        {[1, 2, 3, 4].map(barIndex => {
          const isFilled = filledBars >= barIndex;
          const gradientColors = getGradientColors(isFilled);
          return (
            <linearGradient key={barIndex} id={`gradient-${barIndex}-${uniqueId}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={gradientColors.start} />
              <stop offset="50%" stopColor={gradientColors.start} />
              <stop offset="100%" stopColor={gradientColors.end} />
            </linearGradient>
          );
        })}

        {/* Advanced shadow with depth */}
        <filter id={`shadow-${uniqueId}`}>
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12"/>
          <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.06"/>
        </filter>

        {/* Shimmer effect for active bars */}
        <filter id={`shimmer-${uniqueId}`}>
          <feFlood floodColor="white" floodOpacity="0.25"/>
          <feComposite in="SourceGraphic"/>
        </filter>
      </defs>

      {/* Ed Catmull-inspired sequential bar animation with anticipation and overshoot */}
      {[1, 2, 3, 4].map(barIndex => {
        const barHeights = [6, 9, 12, 15];
        const yPositions = [15, 12, 9, 6];
        const isFilled = filledBars >= barIndex;
        const baseDelay = (barIndex - 1) * 0.15; // Staggered timing

        return (
          <g key={`bar-${barIndex}`}>
            {/* Main bar with Pixar-quality animation */}
            <rect 
              x={2 + (barIndex - 1) * 5} 
              y={yPositions[barIndex - 1]} 
              width="4" 
              height={barHeights[barIndex - 1]} 
              fill={`url(#gradient-${barIndex}-${uniqueId})`}
              rx="2" 
              filter={`url(#shadow-${uniqueId})`}
              opacity={isFilled ? "1" : "0.35"}
            >
              {/* Ed Catmull's 12 Principles of Animation implemented */}

              {/* 1. Anticipation: Small dip before growing */}
              <animate 
                attributeName="height" 
                values={`0;0;${barHeights[barIndex - 1] * 0.8};${barHeights[barIndex - 1] * 1.1};${barHeights[barIndex - 1]}`}
                dur="1.2s" 
                begin={`${0.1 + baseDelay}s`}
                keyTimes="0;0.1;0.6;0.85;1"
                keySplines="0.25 0.1 0.25 1;0.25 0.1 0.25 1;0.25 0.1 0.25 1;0.25 0.1 0.25 1"
                calcMode="spline"
                fill="freeze" 
              />

              {/* 2. Squash and Stretch: Y position with overshoot */}
              <animate 
                attributeName="y" 
                values={`${21};${21};${yPositions[barIndex - 1] + barHeights[barIndex - 1] * 0.2};${yPositions[barIndex - 1] - barHeights[barIndex - 1] * 0.1};${yPositions[barIndex - 1]}`}
                dur="1.2s" 
                begin={`${0.1 + baseDelay}s`}
                keyTimes="0;0.1;0.6;0.85;1"
                keySplines="0.25 0.1 0.25 1;0.25 0.1 0.25 1;0.25 0.1 0.25 1;0.25 0.1 0.25 1"
                calcMode="spline"
                fill="freeze" 
              />

              {/* 3. Staging: Opacity with emotional timing */}
              <animate 
                attributeName="opacity" 
                values={`0;0;0.3;${isFilled ? 1 : 0.35}`}
                dur="1.2s" 
                begin={`${0.1 + baseDelay}s`}
                keyTimes="0;0.1;0.4;1"
                keySplines="0.25 0.1 0.25 1;0.25 0.1 0.25 1;0.25 0.1 0.25 1"
                calcMode="spline"
                fill="freeze" 
              />

              {/* 4. Secondary Animation: Subtle width pulse */}
              <animate 
                attributeName="width" 
                values="4;4;4.3;3.8;4"
                dur="1.2s" 
                begin={`${0.1 + baseDelay}s`}
                keyTimes="0;0.1;0.6;0.85;1"
                keySplines="0.25 0.1 0.25 1;0.25 0.1 0.25 1;0.25 0.1 0.25 1;0.25 0.1 0.25 1"
                calcMode="spline"
                fill="freeze" 
              />

              {/* X position compensation for width change */}
              <animate 
                attributeName="x" 
                values={`${2 + (barIndex - 1) * 5};${2 + (barIndex - 1) * 5};${2 + (barIndex - 1) * 5 - 0.15};${2 + (barIndex - 1) * 5 + 0.1};${2 + (barIndex - 1) * 5}`}
                dur="1.2s" 
                begin={`${0.1 + baseDelay}s`}
                keyTimes="0;0.1;0.6;0.85;1"
                keySplines="0.25 0.1 0.25 1;0.25 0.1 0.25 1;0.25 0.1 0.25 1;0.25 0.1 0.25 1"
                calcMode="spline"
                fill="freeze" 
              />
            </rect>

            {/* Pixar-style shimmer highlight that appears after bar settles */}
            {isFilled && (
              <rect 
                x={2 + (barIndex - 1) * 5} 
                y={yPositions[barIndex - 1]} 
                width="4" 
                height="2" 
                fill="white" 
                rx="2" 
                opacity="0"
              >
                {/* Shimmer reveal after main animation */}
                <animate 
                  attributeName="opacity" 
                  values="0;0;0.4;0.2;0"
                  dur="0.8s" 
                  begin={`${1.0 + baseDelay}s`}
                  keyTimes="0;0.2;0.5;0.8;1"
                  keySplines="0.25 0.1 0.25 1;0.25 0.1 0.25 1;0.25 0.1 0.25 1;0.25 0.1 0.25 1"
                  calcMode="spline"
                  fill="freeze" 
                />

                {/* Shimmer movement */}
                <animate 
                  attributeName="y" 
                  values={`${yPositions[barIndex - 1] + barHeights[barIndex - 1] - 2};${yPositions[barIndex - 1]}`}
                  dur="0.8s" 
                  begin={`${1.0 + baseDelay}s`}
                  fill="freeze" 
                />
              </rect>
            )}

            {/* Elegant particle-like highlight that trails the bar growth */}
            {isFilled && (
              <circle 
                cx={4 + (barIndex - 1) * 5} 
                cy={yPositions[barIndex - 1] - 2} 
                r="1" 
                fill="white" 
                opacity="0"
              >
                {/* Particle appears and fades */}
                <animate 
                  attributeName="opacity" 
                  values="0;0;0.8;0"
                  dur="0.6s" 
                  begin={`${0.8 + baseDelay}s`}
                  keyTimes="0;0.3;0.7;1"
                  keySplines="0.25 0.1 0.25 1;0.25 0.1 0.25 1;0.25 0.1 0.25 1"
                  calcMode="spline"
                  fill="freeze" 
                />

                {/* Particle drifts upward */}
                <animate 
                  attributeName="cy" 
                  values={`${yPositions[barIndex - 1]};${yPositions[barIndex - 1] - 4}`}
                  dur="0.6s" 
                  begin={`${0.8 + baseDelay}s`}
                  fill="freeze" 
                />
              </circle>
            )}
          </g>
        );
      })}

      {/* Final celebration sparkle effect after all bars are complete */}
      {filledBars > 0 && (
        <g opacity="0">
          <animate 
            attributeName="opacity" 
            values="0;1;0"
            dur="0.4s" 
            begin="1.8s"
            fill="freeze" 
          />
          {[...Array(3)].map((_, i) => (
            <circle 
              key={`sparkle-${i}`}
              cx={6 + i * 6} 
              cy={4} 
              r="0.5" 
              fill="white" 
              opacity="0"
            >
              <animate 
                attributeName="opacity" 
                values="0;1;0"
                dur="0.3s" 
                begin={`${1.8 + i * 0.1}s`}
                fill="freeze" 
              />
            </circle>
          ))}
        </g>
      )}
    </svg>
  );
};
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { getConflictTypeLabel } from '../utils/conflictDetection';
import EditVariantNameModal from './EditVariantNameModal';
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const BrainSidebar = ({ 
  categories, 
  integrations = [],
  selectedItem, 
  onItemSelect, 
  onAddClick,
  onEdit,
  onDelete,
  searchQuery,
  onSearchChange,
  conflicts = [],
  isLoadingConflicts = false,
  isProcessingConflicts = false,
  showConflicts = false,
  locations = [],
  onAddLocationVariant,
  onOpenLocationModal,
  onAddIntegration,
  onUpdateIntegration,
  onUpdateMultilingualSettings,
  onDuplicate,
  onCategoryChange, // New prop for handling category changes
  globalMultilingualSettings,
  isMobile = false,
  onMobileSidebarToggle,
  expandedCategories,
  expandedSettings,
  onToggleCategory,
  onToggleSetting,
  onChatToggle,
  isChatOpen = false,
  // New props for Post Call Actions
  postCallActions = [],
  onAddPostCallAction,
  onPostCallActionSelect,
  expandedPostCallActions,
  onTogglePostCallAction,
  // New props for AI Coverage
  coveragePercentage = 0,
  isLoadingCoverage = false,
  onNavigateToAnalysis,
  isAnalysisPanelOpen = false,
  onToggleMixedMessagesModal,
  isMixedMessagesModalOpen = false,
  onStartTutorial, // Add new prop
  // New props for AI Test
  onToggleTestModal,
  isTestModalOpen = false,
  // New props for affected settings filter
  showOnlyAffectedSettings = false,
  affectedSettingIds = new Set(),
  // NEW: Conflict navigation prop
  onOpenConflictForSetting,
  // Clarification Questions
  clarificationQuestions = [],
  // First Message
  onOpenFirstMessageModal,
  // V2 Integration props
  integrationsV2 = [],
  isLoadingV2 = false,
  onConnectIntegrationV2,
  onUpdateIntegrationCredentialsV2,
  onDisconnectIntegrationV2,
  onTriggerSyncV2,
  onToggleIntegrationStatusV2
}) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);

  // Filter dropdown state
  const [currentFilter, setCurrentFilter] = useState('all');
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const filterDropdownRef = useRef(null);

  // Filter options
  const filterOptions = [
    { value: 'all', label: 'All', shortLabel: 'All', icon: FolderOpen },
    { value: 'brain_categories', label: 'Training', shortLabel: 'Training', icon: ChalkboardSimple },
    { value: 'integrations', label: 'Integrations', shortLabel: 'Integrations', icon: Link },
    { value: 'qualifiers', label: 'Qualifiers', shortLabel: 'Qualifiers', icon: Funnel },
    { value: 'post_call_actions', label: 'Post Call Actions', shortLabel: 'Post Call', icon: Timer },
    { value: 'call_settings', label: 'Settings', shortLabel: 'Settings', icon: Gear }
  ];

  // Integration capabilities state
  const [integrationCapabilities, setIntegrationCapabilities] = useState({});

  // Multilingual settings state
  const [multilingualSettings, setMultilingualSettings] = useState({ multilingual: false });
  const [multilingualLoading, setMultilingualLoading] = useState(false);
  const [firstQuestion, setFirstQuestion] = useState('');
  const [firstQuestionLoading, setFirstQuestionLoading] = useState(true);

  // Handle filter dropdown click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setIsFilterDropdownOpen(false);
      }
    };

    if (isFilterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isFilterDropdownOpen]);

  // Get current filter option
  const currentFilterOption = filterOptions.find(option => option.value === currentFilter) || filterOptions[0];

  // Filter visibility logic
  const shouldShowCallSettings = !showOnlyAffectedSettings && (currentFilter === 'all' || currentFilter === 'call_settings');
  const shouldShowIntegrations = !showOnlyAffectedSettings && (currentFilter === 'all' || currentFilter === 'integrations');
  const shouldShowPostCallActions = !showOnlyAffectedSettings && (currentFilter === 'all' || currentFilter === 'post_call_actions');
  const shouldShowBrainCategories = currentFilter === 'all' || currentFilter === 'brain_categories';
  const shouldShowQualifiers = !showOnlyAffectedSettings && (currentFilter === 'all' || currentFilter === 'qualifiers');

  // Function to update multilingual settings locally
  const updateMultilingualSettingsLocal = (newSettings) => {
    setMultilingualSettings(newSettings);
  };

  // Local update function for integration status changes
  const updateIntegrationLocally = (integrationId, updates) => {
    if (onUpdateIntegration) {
      onUpdateIntegration(integrationId, updates);
    }

    // Also update capabilities immediately if status changed
    if (updates.is_active !== undefined || updates.status !== undefined) {
      // Recalculate capabilities for this integration if needed
      const integration = integrations.find(int => int.id === integrationId);
      if (integration) {
        const updatedIntegration = { ...integration, ...updates };
        if (updatedIntegration.status === 'connected' && updatedIntegration.is_active) {
          calculateIntegrationCapabilities(updatedIntegration).then(capabilities => {
            setIntegrationCapabilities(prev => ({
              ...prev,
              [integrationId]: capabilities
            }));
          });
        } else {
          // Remove capabilities if disconnected or inactive
          setIntegrationCapabilities(prev => {
            const newCapabilities = { ...prev };
            delete newCapabilities[integrationId];
            return newCapabilities;
          });
        }
      }
    }
  };

  // Audio refs for chat toggle sounds
  const startAudioRef = useRef(null);
  const endAudioRef = useRef(null);

  // Initialize audio files on component mount
  useEffect(() => {
    // Starting audio
    startAudioRef.current = new Audio('https://storage.thesavants.ai/storage/v1/object/public/audioapp//AiStarting.mp3');
    startAudioRef.current.preload = 'auto';
    startAudioRef.current.volume = 0.3;

    // Ending audio
    endAudioRef.current = new Audio('https://storage.thesavants.ai/storage/v1/object/public/audioapp//AIEnding.mp3');
    endAudioRef.current.preload = 'auto';
    endAudioRef.current.volume = 0.3;

    // Handle audio load errors gracefully
    const handleError = () => {
      console.warn('Audio file failed to load - continuing without sound');
    };

    startAudioRef.current.addEventListener('error', handleError);
    endAudioRef.current.addEventListener('error', handleError);

    return () => {
      if (startAudioRef.current) {
        startAudioRef.current.removeEventListener('error', handleError);
      }
      if (endAudioRef.current) {
        endAudioRef.current.removeEventListener('error', handleError);
      }
    };
  }, []);

  // Fetch multilingual settings
  useEffect(() => {
    const fetchMultilingualSettings = async () => {
      try {
        const { getUserAccountId } = await import('../utils/accountUtils');
        const { accountId, error: accountIdError } = await getUserAccountId();
        if (accountIdError) throw new Error(accountIdError);

        const { data, error } = await supabase
          .from('vapi_call_settings')
          .select('multilingual')
          .eq('account_id', accountId)
          .maybeSingle();

        if (error) {
          if (error.code === 'PGRST116') {
            // No settings found - use defaults
            setMultilingualSettings({ multilingual: false });
          } else {
            throw error;
          }
        } else {
          setMultilingualSettings({
            multilingual: data?.multilingual || false
          });
        }
      } catch (error) {
        console.error('Error fetching multilingual settings:', error);
        setMultilingualSettings({ multilingual: false });
      } finally {
        setMultilingualLoading(false);
      }
    };

    fetchMultilingualSettings();
  }, [supabase]);

  // Fetch first question setting
  useEffect(() => {
    const fetchFirstQuestion = async () => {
      setFirstQuestionLoading(true);
      try {
        const { getUserAccountId } = await import('../utils/accountUtils');
        const { accountId, error: accountIdError } = await getUserAccountId();
        if (accountIdError) throw new Error(accountIdError);

        const { data, error } = await supabase
          .from('vapi_call_settings')
          .select('first_question')
          .eq('account_id', accountId)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        if (data && data.first_question) {
          setFirstQuestion(data.first_question);
        } else {
          setFirstQuestion(''); // Empty if not set, modal will show default
        }
      } catch (error) {
        console.error('Error fetching first question:', error);
        setFirstQuestion('');
      } finally {
        setFirstQuestionLoading(false);
      }
    };

    fetchFirstQuestion();
  }, [supabase]);

  // Handle chat toggle with audio
  const handleChatToggle = async () => {
    if (!isChatOpen) {
      // Opening chat - play start audio
      if (startAudioRef.current) {
        try {
          startAudioRef.current.currentTime = 0;
          await startAudioRef.current.play();
        } catch (error) {
          console.warn('Start audio playback failed:', error);
        }
      }
    } else {
      // Closing chat - play end audio
      if (endAudioRef.current) {
        try {
          endAudioRef.current.currentTime = 0;
          await endAudioRef.current.play();
        } catch (error) {
          console.warn('End audio playback failed:', error);
        }
      }
    }

    // Trigger the actual chat toggle
    onChatToggle();
  };

  const [currentConflictIndex, setCurrentConflictIndex] = useState(0);
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);

  // Enhanced context menu state
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    x: 0,
    y: 0,
    type: null, // 'category', 'setting', 'variant'
    item: null,
    availableActions: []
  });

  // Context menu ref for click outside detection
  const contextMenuRef = useRef(null);



  // Edit variant name modal state
  const [isEditVariantModalOpen, setIsEditVariantModalOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);



  // Mobile long-press state
  const [longPressState, setLongPressState] = useState({
    isPressed: false,
    timeoutId: null,
    target: null
  });

  // Calculate capabilities for integrations
  const calculateIntegrationCapabilities = async (integration) => {
    // Special case: Curbside always has 9 capabilities (fixed)
    if (integration.slug === 'curbside') {
      return { simpleAnswers: 4, complexAnswers: 1, simpleActions: 1, workflows: 3 };
    }

    // For other integrations, calculate from brain data
    const providerId = integration.provider_id; // Use the correct provider ID
    if (!providerId || !categories.length) {
      return { simpleAnswers: 0, complexAnswers: 0, simpleActions: 0, workflows: 0 };
    }

    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError) return { simpleAnswers: 0, complexAnswers: 0, simpleActions: 0, workflows: 0 };

      // Find categories linked to this provider
      const { data: linkedCategories, error: catError } = await supabase
        .from('brain_categories')
        .select('id')
        .eq('account_id', accountId)
        .eq('api_provider_id', providerId);

      if (catError || !linkedCategories?.length) {
        return { simpleAnswers: 0, complexAnswers: 0, simpleActions: 0, workflows: 0 };
      }

      const categoryIds = linkedCategories.map(cat => cat.id);

      // Get all brain_settings for these categories
      const { data: settings, error: settingsError } = await supabase
        .from('brain_settings')
        .select('type')
        .in('category_id', categoryIds)
        .eq('status', 'live');

      if (settingsError) {
        return { simpleAnswers: 0, complexAnswers: 0, simpleActions: 0, workflows: 0 };
      }

      // Count by type
      const counts = settings.reduce((acc, setting) => {
        switch (setting.type) {
          case 'simple_answer':
            acc.simpleAnswers++;
            break;
          case 'complex_answer':
            acc.complexAnswers++;
            break;
          case 'simple_action':
            acc.simpleActions++;
            break;
          case 'workflow':
            acc.workflows++;
            break;
        }
        return acc;
      }, { simpleAnswers: 0, complexAnswers: 0, simpleActions: 0, workflows: 0 });

      return counts;
    } catch (err) {
      console.error('Error calculating integration capabilities:', err);
      return { simpleAnswers: 0, complexAnswers: 0, simpleActions: 0, workflows: 0 };
    }
  };

  // Calculate capabilities for all connected integrations
  useEffect(() => {
    const calculateAllCapabilities = async () => {
      const newCapabilities = {};

      for (const integration of integrations) {
        if (integration.status === 'connected') {
          const capabilities = await calculateIntegrationCapabilities(integration);
          newCapabilities[integration.id] = capabilities;
        }
      }

      setIntegrationCapabilities(newCapabilities);
    };

    if (integrations.length > 0) {
      calculateAllCapabilities();
    }
  }, [integrations, categories]);

  // Reset conflict index when conflicts change
  useEffect(() => {
    setCurrentConflictIndex(0);
  }, [conflicts.length]);

  // Handle context menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the context menu
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setContextMenu({ isOpen: false, x: 0, y: 0, type: null, item: null, availableActions: [] });
      }
    };

    if (contextMenu.isOpen) {
      // Use document to capture clicks anywhere on the page
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [contextMenu.isOpen]);

  // Mobile long-press handlers
  const handleLongPressStart = (e, type, item) => {
    if (!isMobile) return;

    e.preventDefault();

    const timeoutId = setTimeout(() => {
      const actions = getContextMenuActions(type, item);
      setContextMenu({
        isOpen: true,
        x: e.touches ? e.touches[0].clientX : e.clientX,
        y: e.touches ? e.touches[0].clientY : e.clientY,
        type,
        item,
        availableActions: actions
      });
      setLongPressState({ isPressed: false, timeoutId: null, target: null });
    }, 3000);

    setLongPressState({
      isPressed: true,
      timeoutId,
      target: { type, item }
    });
  };

  const handleLongPressEnd = () => {
    if (longPressState.timeoutId) {
      clearTimeout(longPressState.timeoutId);
    }
    setLongPressState({ isPressed: false, timeoutId: null, target: null });
  };

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (longPressState.timeoutId) {
        clearTimeout(longPressState.timeoutId);
      }
    };
  }, [longPressState.timeoutId]);

  // Navigate to next conflict
  const nextConflict = () => {
    setCurrentConflictIndex((prev) => 
      prev < conflicts.length - 1 ? prev + 1 : 0
    );
  };

  // Navigate to previous conflict
  const prevConflict = () => {
    setCurrentConflictIndex((prev) => 
      prev > 0 ? prev - 1 : conflicts.length - 1
    );
  };

  const toggleCategory = (categoryId) => {
    onToggleCategory(categoryId);
  };

  const toggleSetting = (settingId) => {
    onToggleSetting(settingId);
  };

  // Enhanced context menu actions - simplified and functional
  const getContextMenuActions = (type, item) => {
    const closeMenu = () => setContextMenu({ isOpen: false, x: 0, y: 0, type: null, item: null, availableActions: [] });

    switch (type) {
      case 'category':
        return [
          { id: 'edit', label: 'Edit Name', icon: PencilSimple, action: () => { closeMenu(); onEdit(item); } },
          { id: 'separator1', type: 'separator' },
          { id: 'addSetting', label: 'Add New Setting', icon: Plus, action: () => { closeMenu(); onAddClick(item.id); } },
          { id: 'separator2', type: 'separator' },
          { id: 'delete', label: 'Delete Category', icon: Trash, action: () => { closeMenu(); onDelete(item); }, destructive: true }
        ];

      case 'setting':
        const hasLocationVariants = item.locationVariants && item.locationVariants.length > 1;
        const multipleLocationsExist = locations && locations.length > 1;

        const actions = [];

        actions.push({ id: 'duplicate', label: 'Duplicate Setting', icon: Copy, action: () => { closeMenu(); onDuplicate(item); } });
        actions.push({ id: 'separator_dup', type: 'separator' });

        // Location version actions (only if multiple locations exist)
        if (multipleLocationsExist) {
          actions.push({ id: 'addVariant', label: 'Add Location Version', icon: MapPin, action: () => { closeMenu(); onOpenLocationModal(item.id); } });

          if (hasLocationVariants) {
            actions.push({ id: 'manageVariants', label: 'Manage Location Versions', icon: Users, action: () => { closeMenu(); onOpenLocationModal(item.id); } });
          }

          actions.push({ id: 'separator1', type: 'separator' });
        }

        // Standard actions
        actions.push({ id: 'delete', label: 'Delete Setting', icon: Trash, action: () => { closeMenu(); onDelete(item); }, destructive: true });

        return actions;

      case 'variant':
        return [
          { id: 'editName', label: 'Edit Version Name', icon: PencilSimple, action: () => { closeMenu(); handleEditVariantName(item); } },
          { id: 'separator1', type: 'separator' },
          { id: 'addVariant', label: 'Add Location Version', icon: MapPin, action: () => { closeMenu(); onOpenLocationModal(item.parentSetting.id); } },
          { id: 'manageVariants', label: 'Manage Location Versions', icon: Users, action: () => { closeMenu(); onOpenLocationModal(item.parentSetting.id); } },
          { id: 'separator2', type: 'separator' },
          { id: 'delete', label: 'Delete Version', icon: Trash, action: () => { closeMenu(); onDelete(item); }, destructive: true }
        ];

      default:
        return [];
    }
  };

  const showContextMenu = (e, type, item) => {
    e.preventDefault();
    e.stopPropagation();

    const actions = getContextMenuActions(type, item);

    setContextMenu({
      isOpen: true,
      x: e.clientX,
      y: e.clientY,
      type,
      item,
      availableActions: actions
    });
  };

  // Handle editing variant names - now using modal instead of prompt
  const handleEditVariantName = async (variant) => {
    setEditingVariant(variant);
    setIsEditVariantModalOpen(true);
  };

  // Handle variant name save from modal
  const handleVariantNameSave = (updatedVariant) => {
    setEditingVariant(null);
    setIsEditVariantModalOpen(false);
    // No need to refresh - the modal already saved to DB and the UI will update naturally
  };

  // Handle variant name modal close
  const handleVariantNameModalClose = () => {
    setEditingVariant(null);
    setIsEditVariantModalOpen(false);
  };

  // Get location name by ID
  const getLocationName = (locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    return location ? location.name : 'Unknown Location';
  };

  // Get type icon and color based on setting type and action_type (matching CategoryCard.js)
  const getTypeConfig = (type, action_type = null) => {
    const baseTypes = {
      'simple_answer': {
        icon: BookOpen, 
        color: 'bg-blue-100 text-blue-700',
        title: 'Simple Answer'
      },
      'complex_answer': { 
        icon: Database, 
        color: 'bg-purple-100 text-purple-700',
        title: 'Complex Answer'
      },
      'simple_action': {
        icon: Wrench, 
        color: 'bg-amber-100 text-amber-700',
        title: 'Simple Action'
      },
      'complex_action': { 
        icon: GitBranch, 
        color: 'bg-green-100 text-green-700',
        title: 'Complex Action'
      },
      'workflow': { 
        icon: GitBranch, 
        color: 'bg-green-100 text-green-700',
        title: 'Workflow'
      },
      'default': { 
        icon: BookOpen,
        color: 'bg-gray-100 text-gray-700',
        title: 'Unknown Type'
      }
    };

    if (type === 'simple_action' && action_type) {
      const simpleActionBaseConfig = baseTypes.simple_action;
      let specificIcon = simpleActionBaseConfig.icon;
      let specificTitle = simpleActionBaseConfig.title;

      switch (action_type) {
        case 'sms':
          specificIcon = Chat;
          specificTitle = 'SMS Action';
          break;
        case 'email':
          specificIcon = Envelope;
          specificTitle = 'Email Action';
          break;
        case 'transfer':
          specificIcon = PhoneTransfer;
          specificTitle = 'Call Transfer';
          break;
        case 'api':
          specificIcon = WebhooksLogo;
          specificTitle = 'API Action';
          break;
        case 'note':
          specificIcon = Notepad;
          specificTitle = 'Note Action';
          break;
        case 'dtmf':
          specificIcon = Hash;
          specificTitle = 'DTMF Action';
          break;
      }
      return { icon: specificIcon, color: simpleActionBaseConfig.color, title: specificTitle };
    }
    return baseTypes[type] || baseTypes.default;
  };

  // Get icon and color for Post Call Actions
  const getPostCallActionConfig = (actionType) => {
    switch (actionType) {
      case 'sms':
        return { icon: Chat, color: 'bg-amber-100 text-amber-700' };
      case 'email':
        return { icon: Envelope, color: 'bg-amber-100 text-amber-700' };
      case 'transfer':
      case 'call':
        return { icon: PhoneCall, color: 'bg-amber-100 text-amber-700' };
      case 'api':
        return { icon: WebhooksLogo, color: 'bg-amber-100 text-amber-700' };
      default:
        return { icon: Timer, color: 'bg-gray-100 text-gray-700' };
    }
  };

  // Filter categories based on search and affected settings
  const filteredCategories = categories.map(category => {
    let filteredSettings = category.settings || [];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredSettings = filteredSettings.filter(setting => 
        setting.name.toLowerCase().includes(query) ||
        (setting.description && setting.description.toLowerCase().includes(query))
      );
    }

    // Apply affected settings filter
    if (showOnlyAffectedSettings && affectedSettingIds.size > 0) {
      filteredSettings = filteredSettings.filter(setting => 
        affectedSettingIds.has(setting.id)
      );
    }

    // Sort settings alphabetically
    const sortedSettings = filteredSettings.sort((a, b) => 
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );

    // Only hide categories when conflict filter is active and no affected settings
    if (showOnlyAffectedSettings && affectedSettingIds.size > 0 && sortedSettings.length === 0) {
      return null;
    }

    // Always include category in all other cases
    return {
      ...category,
      settings: sortedSettings
    };
  }).filter(Boolean);

  // No longer need to unify, the view model from the hook is already complete.
  const filteredAndSortedIntegrations = integrationsV2
    .filter(integration => {
      if (showOnlyAffectedSettings && affectedSettingIds.size > 0) {
        if (!affectedSettingIds.has(integration.id)) {
          return false;
        }
      }
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        if (
          !integration.name.toLowerCase().includes(query) &&
          !(integration.description && integration.description.toLowerCase().includes(query))
        ) {
          return false;
        }
      }
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));



  return (
    <>
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="w-96 bg-white border-r border-gray-100 flex flex-col h-full overflow-hidden shadow-sm brain-sidebar"
        onWheel={(e) => e.stopPropagation()}
      >
        {/* Elegant Header */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          className={cn("border-b border-gray-100 bg-white shadow-sm", isMobile ? "p-3" : "p-5")}
        >
          {/* Mobile Layout - Filter dropdown on first line, icons on second line */}
          {isMobile ? (
            <>
              {/* First Line - Filter Dropdown */}
              <div className="flex items-center justify-between mb-3">
                {/* Filter Dropdown */}
                <div className="relative" ref={filterDropdownRef}>
                  <div className="flex items-center space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMobileSidebarToggle();
                      }}
                      className="w-8 h-8 rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md flex items-center justify-center transition-all duration-200"
                    >
                      <ArrowRight className="w-4 h-4 text-gray-600" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                      className={cn(
                        "flex items-center rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 space-x-3 px-4 py-3 text-sm",
                        isFilterDropdownOpen ? "bg-gray-50 border-gray-300" : "hover:bg-gray-50"
                      )}
                    >
                      <div className="bg-gray-100/80 rounded-lg p-1.5">
                        <currentFilterOption.icon className="text-gray-700 w-5 h-5" />
                      </div>
                      <span className="font-light text-gray-900 tracking-tight">{currentFilterOption.shortLabel}</span>
                      <motion.div
                        animate={{ rotate: isFilterDropdownOpen ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <CaretDown className="text-gray-500 w-5 h-5" />
                      </motion.div>
                    </motion.button>
                  </div>

                  {/* Dropdown Menu */}
                  <AnimatePresence>
                    {isFilterDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                        className="absolute top-full left-0 mt-2 w-full min-w-[200px] bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50"
                      >
                        {filterOptions.map((option) => {
                          const OptionIcon = option.icon;
                          return (
                            <motion.button
                              key={option.value}
                              whileHover={{ backgroundColor: '#f9fafb' }}
                              onClick={() => {
                                setCurrentFilter(option.value);
                                setIsFilterDropdownOpen(false);
                              }}
                              className={cn(
                                "flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-all font-light",
                                currentFilter === option.value 
                                  ? "bg-blue-50 text-blue-700" 
                                  : "text-gray-700 hover:text-gray-900"
                              )}
                            >
                              <div className={cn(
                                "p-1.5 rounded-lg",
                                currentFilter === option.value 
                                  ? "bg-blue-100/80" 
                                  : "bg-gray-100/80"
                              )}>
                                <OptionIcon className={cn(
                                  "w-3 h-3",
                                  currentFilter === option.value 
                                    ? "text-blue-600" 
                                    : "text-gray-700"
                                )} />
                              </div>
                              <span className="truncate">{option.label}</span>
                              {currentFilter === option.value && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="ml-auto w-2 h-2 bg-blue-500 rounded-full"
                                />
                              )}
                            </motion.button>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Second Line - Icons */}
              <div className="flex items-center justify-start space-x-2 mb-3">
                {/* Tutorial Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onStartTutorial}
                  className="rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 hover:bg-gray-50 text-gray-600 p-2.5"
                  aria-label="Start tutorial"
                >
                  <Lightbulb className="w-5 h-5" />
                </motion.button>

                {/* Modern Conflict Notification */}
                {showConflicts && (
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onToggleMixedMessagesModal}
                    className={cn(
                      "relative rounded-xl transition-all duration-200 border border-gray-200 bg-white shadow-sm hover:shadow-md p-2.5",
                      isMixedMessagesModalOpen ? "bg-red-50 text-red-600 border-red-200" : "hover:bg-gray-50 text-gray-600"
                    )}
                    aria-label={conflicts.length > 0 ? `${conflicts.length} conflict${conflicts.length !== 1 ? 's' : ''} detected` : 'No conflicts detected'}
                  >
                    <ShieldWarning className={cn(
                      "w-5 h-5",
                      isMixedMessagesModalOpen || conflicts.length > 0 ? "text-red-600" : "text-gray-600"
                    )} />

                    {/* Modern notification badge */}
                    {(conflicts.length > 0 || isProcessingConflicts) && (
                      <motion.span 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        className="absolute bg-red-500 text-white rounded-full flex items-center justify-center font-medium shadow-lg border-2 border-white -top-1 -right-1 h-7 w-7 text-xs"
                      >
                        {isProcessingConflicts ? (
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                          >
                            <CircleNotch className="h-4 w-4" />
                          </motion.div>
                        ) : (
                          conflicts.length
                        )}
                      </motion.span>
                    )}
                  </motion.button>
                )}

                {/* AI Coverage Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onNavigateToAnalysis}
                  className={cn(
                    "rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 p-2.5",
                    isAnalysisPanelOpen 
                      ? (() => {
                          if (coveragePercentage === 0 || coveragePercentage < 30) {
                            return "bg-red-100 text-red-600 border-red-200";
                          } else if (coveragePercentage >= 80) {
                            return "bg-green-100 text-green-600 border-green-200";
                          } else {
                            return "bg-yellow-100 text-yellow-600 border-yellow-200";
                          }
                        })()
                      : "hover:bg-gray-50 text-gray-600"
                  )}
                  aria-label={`AI Coverage: ${coveragePercentage}%`}
                >
                  <DynamicBarChart 
                    percentage={coveragePercentage} 
                    size={20}
                    isLoading={isLoadingCoverage}
                  />
                </motion.button>

                {/* Test AI Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onToggleTestModal}
                  className={cn(
                    "rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 p-2.5",
                    isTestModalOpen ? "bg-green-50 text-green-600 border-green-200" : "hover:bg-gray-50 text-gray-600"
                  )}
                  aria-label="Test Your AI"
                >
                  <Microphone className="w-5 h-5" />
                </motion.button>

                {/* Android Logo Button */}
                {onChatToggle && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleChatToggle}
                    className={cn(
                      "rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 ai-assistant-button p-2.5",
                      isChatOpen ? "bg-blue-50 text-blue-600 border-blue-200" : "hover:bg-gray-50 text-gray-600"
                    )}
                    aria-label={isChatOpen ? "Close AI Assistant" : "Open AI Assistant"}
                  >
                    <AndroidLogo className="w-5 h-5" />
                  </motion.button>
                )}
              </div>
            </>
          ) : (
            /* Desktop Layout - Original single line layout */
            <div className="flex items-center justify-between mb-4">
              {/* Filter Dropdown */}
              <div className="relative" ref={filterDropdownRef}>
                <div className="flex items-center space-x-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                    className={cn(
                      "flex items-center rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 space-x-2 px-3 py-2 text-sm",
                      isFilterDropdownOpen ? "bg-gray-50 border-gray-300" : "hover:bg-gray-50"
                    )}
                  >
                    <div className="bg-gray-100/80 rounded-lg p-1">
                      <currentFilterOption.icon className="text-gray-700 w-3 h-3" />
                    </div>
                    <span className="font-light text-gray-900 tracking-tight">{currentFilterOption.shortLabel}</span>
                    <motion.div
                      animate={{ rotate: isFilterDropdownOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <CaretDown className="text-gray-500 w-3 h-3" />
                    </motion.div>
                  </motion.button>
                </div>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {isFilterDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                      className="absolute top-full left-0 mt-2 w-full min-w-[200px] bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50"
                    >
                      {filterOptions.map((option) => {
                        const OptionIcon = option.icon;
                        return (
                          <motion.button
                            key={option.value}
                            whileHover={{ backgroundColor: '#f9fafb' }}
                            onClick={() => {
                              setCurrentFilter(option.value);
                              setIsFilterDropdownOpen(false);
                            }}
                            className={cn(
                              "flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-all font-light",
                              currentFilter === option.value 
                                ? "bg-blue-50 text-blue-700" 
                                : "text-gray-700 hover:text-gray-900"
                            )}
                          >
                            <div className={cn(
                              "p-1.5 rounded-lg",
                              currentFilter === option.value 
                                ? "bg-blue-100/80" 
                                : "bg-gray-100/80"
                            )}>
                              <OptionIcon className={cn(
                                "w-3 h-3",
                                currentFilter === option.value 
                                  ? "text-blue-600" 
                                  : "text-gray-700"
                              )} />
                            </div>
                            <span className="truncate">{option.label}</span>
                            {currentFilter === option.value && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="ml-auto w-2 h-2 bg-blue-500 rounded-full"
                              />
                            )}
                          </motion.button>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="flex items-center space-x-2">
                {/* Tutorial Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onStartTutorial}
                  className="rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 hover:bg-gray-50 text-gray-600 p-2"
                  aria-label="Start tutorial"
                >
                  <Lightbulb className="w-4 h-4" />
                </motion.button>

                {/* Modern Conflict Notification */}
                {showConflicts && (
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onToggleMixedMessagesModal}
                    className={cn(
                      "relative rounded-xl transition-all duration-200 border border-gray-200 bg-white shadow-sm hover:shadow-md p-2",
                      isMixedMessagesModalOpen ? "bg-red-50 text-red-600 border-red-200" : "hover:bg-gray-50 text-gray-600"
                    )}
                    aria-label={conflicts.length > 0 ? `${conflicts.length} conflict${conflicts.length !== 1 ? 's' : ''} detected` : 'No conflicts detected'}
                  >
                    <ShieldWarning className={cn(
                      "w-4 h-4",
                      isMixedMessagesModalOpen || conflicts.length > 0 ? "text-red-600" : "text-gray-600"
                    )} />

                    {/* Modern notification badge */}
                    {(conflicts.length > 0 || isProcessingConflicts) && (
                      <motion.span 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        className="absolute bg-red-500 text-white rounded-full flex items-center justify-center font-medium shadow-lg border-2 border-white -top-1 -right-1 h-5 w-5 text-xs"
                      >
                        {isProcessingConflicts ? (
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                          >
                            <CircleNotch className="h-3 w-3" />
                          </motion.div>
                        ) : (
                          conflicts.length
                        )}
                      </motion.span>
                    )}
                  </motion.button>
                )}

                {/* AI Coverage Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onNavigateToAnalysis}
                  className={cn(
                    "rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 p-2",
                    isAnalysisPanelOpen 
                      ? (() => {
                          if (coveragePercentage === 0 || coveragePercentage < 30) {
                            return "bg-red-100 text-red-600 border-red-200";
                          } else if (coveragePercentage >= 80) {
                            return "bg-green-100 text-green-600 border-green-200";
                          } else {
                            return "bg-yellow-100 text-yellow-600 border-yellow-200";
                          }
                        })()
                      : "hover:bg-gray-50 text-gray-600"
                  )}
                  aria-label={`AI Coverage: ${coveragePercentage}%`}
                >
                  <DynamicBarChart 
                    percentage={coveragePercentage} 
                    size={16}
                    isLoading={isLoadingCoverage}
                  />
                </motion.button>

                {/* Test AI Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onToggleTestModal}
                  className={cn(
                    "rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 p-2",
                    isTestModalOpen ? "bg-green-50 text-green-600 border-green-200" : "hover:bg-gray-50 text-gray-600"
                  )}
                  aria-label="Test Your AI"
                >
                  <Microphone className="w-4 h-4" />
                </motion.button>

                {/* Android Logo Button */}
                {onChatToggle && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleChatToggle}
                    className={cn(
                      "rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 ai-assistant-button p-2",
                      isChatOpen ? "bg-blue-50 text-blue-600 border-blue-200" : "hover:bg-gray-50 text-gray-600"
                    )}
                    aria-label={isChatOpen ? "Close AI Assistant" : "Open AI Assistant"}
                  >
                    <AndroidLogo className="w-4 h-4" />
                  </motion.button>
                )}
              </div>
            </div>
          )}

          {/* Elegant Search Bar */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="relative"
          >
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <MagnifyingGlass className={cn(isMobile ? "h-4 w-4" : "h-4 w-4", "text-gray-400")} />
            </div>
            <input
              type="text"
              className={cn(
                "block w-full border border-gray-200 rounded-xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:placeholder-gray-300 focus:ring-1 focus:ring-gray-300 focus:border-gray-300 transition-all",
                isMobile ? "pl-12 pr-4 py-3 text-base" : "pl-12 pr-4 py-3 text-sm",
                showOnlyAffectedSettings && "border-red-200 bg-red-50"
              )}
              placeholder={showOnlyAffectedSettings ? "Filtered to affected settings..." : "Search brain..."}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />

            {/* Filter indicator */}
            {showOnlyAffectedSettings && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute inset-y-0 right-0 pr-4 flex items-center"
              >
                <div className="flex items-center space-x-1 text-red-600 text-xs font-medium">
                  <ShieldWarning className="w-3 h-3" />
                  <span>Filtered</span>
                </div>
              </motion.div>
            )}
          </motion.div>
        </motion.div>

        {/* Categories List */}
        <div className="flex-1 overflow-y-auto overscroll-none bg-gray-50">
            <div className={cn(isMobile ? "px-4 pb-4" : "px-6 pb-6")}>

            {/* Training Section */}
            {shouldShowBrainCategories && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                {/* Training Header */}
                <div className="px-5 py-4 bg-white border-t border-b border-gray-100 -mx-6">
                  <motion.div 
                    className="flex items-center justify-between"
                    whileTap={{ scale: 0.99 }}
                    onClick={onAddClick}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-1.5 bg-gray-100/80 rounded-lg">
                        <ChalkboardSimple className="w-4 h-4 text-gray-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-base text-gray-900">Training</h3>
                        <p className="text-xs text-gray-500 mt-0.5">Train your answers and actions</p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddClick();
                      }}
                      className="p-1.5 rounded-lg border border-gray-200 bg-white shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-200 add-category-button"
                      aria-label="Add Training Content"
                    >
                      <Plus className="w-3.5 h-3.5 text-gray-600" />
                    </motion.button>
                  </motion.div>
                </div>
              </motion.div>
            )}

            {/* Training Empty State */}
            {shouldShowBrainCategories && categories.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.3 }}
                className="border-t border-b border-gray-100 -mx-6"
              >
                <div className="flex flex-col items-center justify-center py-8 px-6 text-center">
                  <div className="p-3 bg-gray-100/80 rounded-xl mb-3">
                    <ChalkboardSimple className="w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">No training content yet</p>
                  <p className="text-xs text-gray-400">Add answers and actions to train your AI</p>
                </div>
              </motion.div>
            )}

            {shouldShowBrainCategories && filteredCategories.map((category, categoryIndex) => (
                  <motion.div 
                    key={category.id} 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 + categoryIndex * 0.05, duration: 0.4 }}
                  >
                {/* Category Item */}
                <div className="border-t border-b border-gray-100 divide-y divide-gray-100 -mx-6">
                  <div
                    className={cn(
                      "flex items-center justify-between px-6 py-2.5 bg-white hover:bg-gray-50 transition-colors group",
                      selectedItem?.id === category.id && selectedItem?.type === 'category' && "bg-blue-50"
                    )}
                    onClick={() => {
                      toggleCategory(category.id);
                    }}
                    onContextMenu={(e) => showContextMenu(e, 'category', category)}
                  >
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      {expandedCategories.has(category.id) ? (
                        <CaretDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      ) : (
                        <CaretRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      )}
                      <span className="text-gray-800 text-sm font-light truncate">{category.name}</span>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1, backgroundColor: '#f3f4f6' }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        showContextMenu(e, 'category', category);
                      }}
                      className="text-gray-500 hover:text-gray-700 rounded-full w-7 h-7 flex items-center justify-center hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      aria-label={`Options for ${category.name}`}
                    >
                      <DotsThreeVertical size={14} />
                    </motion.button>
                  </div>

                {/* Settings List */}
                    <AnimatePresence>
                  {expandedCategories.has(category.id) && category.settings && (
                        <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                      className="overflow-hidden divide-y divide-gray-100"
                        >
                          {category.settings.map((setting, settingIndex) => {
                        const typeConfig = getTypeConfig(setting.original_type || setting.type, setting.action_type);
                            const IconComponent = typeConfig.icon;
                        const hasLocationVariants = setting.locationVariants && setting.locationVariants.length > 1;
                            const isSettingExpanded = expandedSettings.has(setting.id);

                            return (
                              <motion.div 
                                key={setting.id}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: settingIndex * 0.05, duration: 0.3 }}
                              >
                                {/* Setting Item */}
                                <div
                                  className={cn(
                                    "flex items-center justify-between px-8 py-2.5 bg-white hover:bg-gray-50 transition-colors group",
                                    selectedItem?.id === setting.id && selectedItem?.type === 'setting' && affectedSettingIds.has(setting.id) && "border-l-2 border-red-200 bg-red-50/30",
                                    selectedItem?.id === setting.id && selectedItem?.type === 'setting' && !affectedSettingIds.has(setting.id) && "bg-blue-50"
                                  )}
                                  onClick={() => {
                                    onItemSelect({ ...setting, type: 'setting', category_name: category.name });
                                  }}
                                  onContextMenu={(e) => showContextMenu(e, 'setting', setting)}
                                >
                                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                                    <div className={cn(
                                      "w-5 h-5 rounded flex items-center justify-center flex-shrink-0", 
                                      affectedSettingIds.has(setting.id) ? "bg-red-100 text-red-700" : typeConfig.color
                                    )}>
                                      <IconComponent className="w-3 h-3" />
                                    </div>
                                    <span className={cn(
                                      "text-sm font-light truncate",
                                      affectedSettingIds.has(setting.id) ? "text-red-700" : "text-gray-800"
                                    )}>{setting.name}</span>
                                  </div>

                                  <div className="flex items-center space-x-1">
                                    {/* Location Variant Count */}
                                    {setting.locationVariants && setting.locationVariants.some(v => v.assignedLocationIds && v.assignedLocationIds.length > 0) && (
                                      <div className="flex items-center space-x-1 text-xs text-gray-500 mr-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <span>{setting.locationVariants.length}</span>
                                        <MapPinArea className="w-3.5 h-3.5" />
                                      </div>
                                    )}
                                    <motion.button
                                      whileHover={{ scale: 1.1, backgroundColor: '#f3f4f6' }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        showContextMenu(e, 'setting', setting);
                                      }}
                                      className="text-gray-500 hover:text-gray-700 rounded-full w-7 h-7 flex items-center justify-center hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                      aria-label={`Options for ${setting.name}`}
                                    >
                                      <DotsThreeVertical size={14} />
                                    </motion.button>
                                  </div>
                                </div>

                                {/* Location Variants - REMOVED from sidebar */}
                              </motion.div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                </div>
                  </motion.div>
            ))}

            {/* Integrations Section */}
            {shouldShowIntegrations && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + filteredCategories.length * 0.05, duration: 0.4 }}
              >
                {/* Integrations Header */}
                <div className="px-5 py-4 bg-white border-t border-b border-gray-100 -mx-6">
                  <motion.div 
                    className="flex items-center justify-between"
                    whileTap={{ scale: 0.99 }}
                    onClick={onAddIntegration}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-1.5 bg-gray-100/80 rounded-lg">
                        <Link className="w-4 h-4 text-gray-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-base text-gray-900">Integrations</h3>
                        <p className="text-xs text-gray-500 mt-0.5">Connect your business tools</p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddIntegration();
                      }}
                      className="p-1.5 rounded-lg border border-gray-200 bg-white shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-200"
                      aria-label="Add Integration"
                    >
                      <Plus className="w-3.5 h-3.5 text-gray-600" />
                    </motion.button>
                  </motion.div>
                </div>

                {/* Connected Integrations and Add Button */}
                {filteredAndSortedIntegrations.length > 0 ? (
                  <div className="border-t border-b border-gray-100 divide-y divide-gray-100 -mx-6">
                    {filteredAndSortedIntegrations.map((integration, integrationIndex) => {
                      const isConflicted = affectedSettingIds.has(integration.id);

                      // Conditional rendering for V2 vs legacy integrations
                      if (integration.usesV2Backend) {
                        return (
                          <motion.div
                            key={integration.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.45 + filteredCategories.length * 0.05 + integrationIndex * 0.1, duration: 0.3 }}
                            className={cn(
                              "flex items-center justify-between px-6 py-2.5 bg-white hover:bg-gray-50 transition-colors group",
                              selectedItem?.id === integration.id && selectedItem?.type === 'integration' && !isConflicted && "bg-blue-50",
                              !integration.is_active && integration.status === 'connected' && "opacity-60",
                              isConflicted && "bg-red-50/30",
                              selectedItem?.id === integration.id && isConflicted && "border-l-2 border-red-200"
                            )}
                            onClick={() => {
                              onItemSelect({ ...integration, type: 'integration' });
                            }}
                          >
                            <span className={cn("text-gray-800 text-sm font-light mr-2", isConflicted && "text-red-700")}>{integration.name}</span>
                            <div className="flex items-center space-x-1">
                              {integration.isSyncing && (
                                <CircleNotch className="w-4 h-4 text-blue-500 animate-spin mr-1" />
                              )}
                              {integration.status === 'CONNECTED' ? (
                                <Link className={cn(
                                  "w-4 h-4",
                                  isConflicted ? "text-red-600" : (integration.connection?.is_enabled === false ? "text-gray-400" : "text-green-500")
                                )} />
                              ) : (
                                <LinkBreak className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </motion.div>
                        );
                      } else {
                        // Legacy integration rendering (keep existing logic)
                      return (
                        <motion.div
                          key={integration.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.45 + filteredCategories.length * 0.05 + integrationIndex * 0.1, duration: 0.3 }}
                          className={cn(
                            "flex items-center justify-between px-6 py-2.5 bg-white hover:bg-gray-50 transition-colors group",
                            selectedItem?.id === integration.id && selectedItem?.type === 'integration' && !isConflicted && "bg-blue-50",
                            !integration.is_active && integration.status === 'connected' && "opacity-60",
                            isConflicted && "bg-red-50/30",
                            selectedItem?.id === integration.id && isConflicted && "border-l-2 border-red-200"
                          )}
                          onClick={() => {
                            onItemSelect({ ...integration, type: 'integration' });
                          }}
                        >
                          <span className={cn("text-gray-800 text-sm font-light mr-2", isConflicted && "text-red-700")}>{integration.name}</span>
                          <div className="flex items-center space-x-1">
                            {integration.status === 'connected' ? (
                              <Link className={cn(
                                "w-4 h-4",
                                isConflicted ? "text-red-600" : (!integration.is_active ? "text-gray-400" : "text-green-500")
                              )} />
                            ) : (
                              <LinkBreak className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                        </motion.div>
                      );
                      }
                    })}
                  </div>
                ) : (
                  // Only show empty state if not filtering by conflict and not searching
                  !showOnlyAffectedSettings && !searchQuery.trim() && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.45 + filteredCategories.length * 0.05, duration: 0.3 }}
                      className="border-t border-b border-gray-100 -mx-6"
                    >
                      <div className="flex flex-col items-center justify-center py-8 px-6 text-center">
                        <div className="p-3 bg-gray-100/80 rounded-xl mb-3">
                          <Link className="w-5 h-5 text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-500 mb-1">No integrations connected</p>
                        <p className="text-xs text-gray-400">Connect tools to enhance your AI's capabilities</p>
                      </div>
                    </motion.div>
                  )
                )}

              </motion.div>
            )}

            {/* Qualifiers Section */}
            {shouldShowQualifiers && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + filteredCategories.length * 0.05 + integrations.length * 0.1, duration: 0.4 }}
              >
                {/* Header */}
                <div className="px-5 py-4 bg-white border-t border-b border-gray-100 -mx-6">
                  <motion.div 
                    className="flex items-center justify-between"
                    whileTap={{ scale: 0.99 }}
                    onClick={() => onItemSelect({ 
                      id: 'new', 
                      type: 'clarification_question', 
                      isNew: true,
                      topic_name: 'New Qualifier',
                      question_to_ask: '',
                      possible_answers: []
                    })}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-1.5 bg-gray-100/80 rounded-lg">
                        <Funnel className="w-4 h-4 text-gray-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-base text-gray-900">Qualifiers</h3>
                        <p className="text-xs text-gray-500 mt-0.5">Qualify user intent for answers and actions</p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onItemSelect({ 
                          id: 'new', 
                          type: 'clarification_question', 
                          isNew: true,
                          topic_name: 'New Qualifier',
                          question_to_ask: '',
                          possible_answers: []
                        });
                      }}
                      className="p-1.5 rounded-lg border border-gray-200 bg-white shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-200"
                      aria-label="Add Qualifier"
                    >
                      <Plus className="w-3.5 h-3.5 text-gray-600" />
                    </motion.button>
                  </motion.div>
                </div>

                {/* List */}
                {clarificationQuestions.length > 0 ? (
                  <div className="border-t border-b border-gray-100 divide-y divide-gray-100 -mx-6">
                    {clarificationQuestions.map((question, index) => (
                      <motion.div
                        key={question.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.55 + filteredCategories.length * 0.05 + integrations.length * 0.1 + index * 0.05, duration: 0.3 }}
                        className={cn(
                          "flex items-center justify-between px-6 py-2.5 bg-white hover:bg-gray-50 transition-colors group",
                          selectedItem?.id === question.id && selectedItem?.type === 'clarification_question' && "bg-blue-50"
                        )}
                        onClick={() => onItemSelect({ ...question, type: 'clarification_question' })}
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className={cn("w-5 h-5 rounded flex items-center justify-center flex-shrink-0", 'bg-sky-100 text-sky-700')}>
                            <Funnel className="w-3 h-3" />
                          </div>
                          <span className="text-gray-800 text-sm font-light truncate">{question.topic_name}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.55 + filteredCategories.length * 0.05 + integrations.length * 0.1, duration: 0.3 }}
                    className="border-t border-b border-gray-100 -mx-6"
                  >
                    <div className="flex flex-col items-center justify-center py-8 px-6 text-center">
                      <div className="p-3 bg-gray-100/80 rounded-xl mb-3">
                        <Funnel className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500 mb-1">No qualifiers yet</p>
                      <p className="text-xs text-gray-400">Add questions to qualify user intent.</p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* Post Call Actions Section */}
            {shouldShowPostCallActions && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + filteredCategories.length * 0.05 + integrations.length * 0.1 + postCallActions.length * 0.05, duration: 0.4 }}
              >
                {/* Post Call Actions Header */}
                <div className="px-5 py-4 bg-white border-t border-b border-gray-100 -mx-6">
                  <motion.div 
                    className="flex items-center justify-between"
                    whileTap={{ scale: 0.99 }}
                    onClick={() => onPostCallActionSelect({ 
                      id: 'new', 
                      type: 'postCallAction', 
                      isNew: true,
                      name: 'New Post Call Action',
                      enabled: true,
                      actionType: 'email',
                      triggerType: 'always'
                    })}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-1.5 bg-gray-100/80 rounded-lg">
                        <Timer className="w-4 h-4 text-gray-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-base text-gray-900">Post Call Actions</h3>
                        <p className="text-xs text-gray-500 mt-0.5">Automate tasks after calls</p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPostCallActionSelect({ 
                          id: 'new', 
                          type: 'postCallAction', 
                          isNew: true,
                          name: 'New Post Call Action',
                          enabled: true,
                          actionType: 'email',
                          triggerType: 'always'
                        });
                      }}
                      className="p-1.5 rounded-lg border border-gray-200 bg-white shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-200"
                      aria-label="Add Post Call Action"
                    >
                      <Plus className="w-3.5 h-3.5 text-gray-600" />
                    </motion.button>
                  </motion.div>
                </div>

                {/* Post Call Actions List */}
                {postCallActions.length > 0 ? (
                  <div className="border-t border-b border-gray-100 divide-y divide-gray-100 -mx-6">
                    {postCallActions.map((action, actionIndex) => {
                      const actionConfig = getPostCallActionConfig(action.actionType);
                      const IconComponent = actionConfig.icon;

                      return (
                        <motion.div
                          key={action.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.65 + filteredCategories.length * 0.05 + integrations.length * 0.1 + actionIndex * 0.05, duration: 0.3 }}
                          className={cn(
                            "flex items-center justify-between px-6 py-2.5 bg-white hover:bg-gray-50 transition-colors group",
                            selectedItem?.id === action.id && selectedItem?.type === 'postCallAction' && "bg-blue-50",
                            !action.enabled && "opacity-60"
                          )}
                          onClick={() => onPostCallActionSelect({ ...action, type: 'postCallAction' })}
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className={cn("w-5 h-5 rounded flex items-center justify-center flex-shrink-0", actionConfig.color)}>
                              <IconComponent className="w-3 h-3" />
                            </div>
                            <span className="text-gray-800 text-sm font-light truncate">{action.name}</span>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.65 + filteredCategories.length * 0.05 + integrations.length * 0.1, duration: 0.3 }}
                    className="border-t border-b border-gray-100 -mx-6"
                  >
                    <div className="flex flex-col items-center justify-center py-8 px-6 text-center">
                      <div className="p-3 bg-gray-100/80 rounded-xl mb-3">
                        <Timer className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500 mb-1">No post-call actions set</p>
                      <p className="text-xs text-gray-400">Automate follow-up tasks after calls end</p>
                    </div>
                  </motion.div>
                )}

              </motion.div>
            )}

            {/* Settings Section */}
            {shouldShowCallSettings && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + filteredCategories.length * 0.05, duration: 0.4 }}
              >
                {/* Settings Header */}
                <div className="px-5 py-4 bg-white border-t border-b border-gray-100 -mx-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-1.5 bg-gray-100/80 rounded-lg">
                      <Gear className="w-4 h-4 text-gray-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-base text-gray-900">Settings</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Configure AI behavior</p>
                    </div>
                  </div>
                </div>

                {/* Settings List */}
                <div className="border-t border-b border-gray-100 divide-y divide-gray-100 -mx-6">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.75 + filteredCategories.length * 0.05, duration: 0.3 }}
                    className={cn(
                      "flex items-center justify-between px-6 py-2.5 bg-white hover:bg-gray-50 transition-colors group",
                      selectedItem?.type === 'multilingual' && "bg-blue-50"
                    )}
                    onClick={() => onItemSelect({ id: 'multilingual', type: 'multilingual' })}
                  >
                    <span className="text-gray-800 text-sm font-light mr-2">Multilingual Support</span>
                    <div className="flex items-center space-x-1">
                      {multilingualLoading ? (
                        <CircleNotch className="w-4 h-4 text-gray-400 animate-spin" />
                      ) : (globalMultilingualSettings?.multilingual ?? multilingualSettings.multilingual) ? (
                        <Globe className="w-4 h-4 text-green-500" />
                      ) : (
                        <GlobeX className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.80 + filteredCategories.length * 0.05, duration: 0.3 }}
                    className={cn(
                      "flex items-center justify-between px-6 py-2.5 bg-white hover:bg-gray-50 transition-colors group",
                      selectedItem?.type === 'firstMessage' && "bg-blue-50"
                    )}
                    onClick={() => onItemSelect({ id: 'firstMessage', type: 'firstMessage' })}
                  >
                    <span className="text-gray-800 text-sm font-light mr-2">First Message</span>
                    <div className="flex items-center space-x-1">
                      {firstQuestionLoading ? (
                        <CircleNotch className="w-4 h-4 text-gray-400 animate-spin" />
                      ) : (
                        <ChatTeardropText className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                  </motion.div>
                </div>

              </motion.div>
            )}
            </div>
        </div>
      </motion.div>

      {/* Enhanced Context Menu */}
      {contextMenu.isOpen && (
        <motion.div
          ref={contextMenuRef}
          initial={{ opacity: 0, scale: 0.95, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="fixed bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 min-w-[180px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          {contextMenu.availableActions.map((action) => {
            if (action.type === 'separator') {
              return <div key={action.id} className="border-t border-gray-100 my-1" />;
            }

            return (
              <motion.button
                key={action.id}
                whileHover={{ scale: 1.01, backgroundColor: action.destructive ? '#fef2f2' : '#f9fafb' }}
                whileTap={{ scale: 0.98 }}
                className={cn(
                  "flex items-center px-3 py-2.5 text-sm w-full text-left transition-all font-light",
                  action.disabled && "opacity-50 cursor-not-allowed",
                  action.destructive ? "text-red-600 hover:text-red-700 hover:bg-red-50" : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!action.disabled && action.action) {
                    action.action();
                  }
                }}
                disabled={action.disabled}
              >
                <action.icon className="w-4 h-4 flex-shrink-0 mr-3" />
                <span className="truncate">{action.label}</span>
                {action.disabled && (
                  <span className="ml-auto text-xs text-gray-400 font-light">Soon</span>
                )}
              </motion.button>
            );
          })}
        </motion.div>
      )}

      {/* Edit Variant Name Modal */}
      <EditVariantNameModal
        isOpen={isEditVariantModalOpen}
        onClose={handleVariantNameModalClose}
        onSave={handleVariantNameSave}
        variant={editingVariant}
      />





    </>
  );
};

export default BrainSidebar;